
<template>
  <section class="calculator-module">
    <div class="module-header">
      <h2>{{ t('elements.title') }}</h2>
      <span class="toggle-icon">▼</span>
    </div>
    <div class="module-content">
      <div class="calculator-intro">
        <h3>🧪 {{ t('elements.title') }}</h3>
        <p>{{ t('elements.subtitle') }}</p>
      </div>
      
      <div class="calculator-tabs">
        <button
          class="tab-btn"
          :class="{ active: activeElement === 'alk' }"
          @click="activeElement = 'alk'"
        >
          {{ t('elements.tabs.alk') }}
        </button>
        <button
          class="tab-btn"
          :class="{ active: activeElement === 'ca' }"
          @click="activeElement = 'ca'"
        >
          {{ t('elements.tabs.ca') }}
        </button>
        <button
          class="tab-btn"
          :class="{ active: activeElement === 'mg' }"
          @click="activeElement = 'mg'"
        >
          {{ t('elements.tabs.mg') }}
        </button>
        <button
          class="tab-btn"
          :class="{ active: activeElement === 'k' }"
          @click="activeElement = 'k'"
        >
          {{ t('elements.tabs.k') }}
        </button>
      </div>
      
      <!-- 碱度计算 -->
      <div v-if="activeElement === 'alk'" class="calculator-form">
        <div class="form-section">
          <h4>{{ t('elements.labels.volume') }}</h4>
          <div class="form-group">
            <label for="alk_volume">{{ t('elements.labels.volume') }}</label>
            <div class="input-with-action">
              <input
                type="number"
                id="alk_volume"
                v-model="alkData.volume"
                class="form-control"
                :placeholder="t('elements.placeholders.volume').value"
                step="0.1"
                min="1"
              >
              <button
                v-if="storedWaterVolume"
                class="use-stored-btn"
                @click="useStoredVolume('alk')"
                type="button"
              >
                {{ t('elements.labels.useStoredVolume') }}
              </button>
            </div>
            <small class="form-hint" v-if="storedWaterVolume">
              {{ t('elements.labels.storedVolume') }}: {{ storedWaterVolume }} L
            </small>
          </div>
        </div>
        
        <div class="form-section">
          <h4>{{ t('elements.sections.waterParams') }}</h4>
          <div class="form-row">
            <div class="form-group">
              <label for="alk_current">{{ t('elements.labels.currentAlk') }}</label>
              <input 
                type="number" 
                id="alk_current" 
                v-model="alkData.current" 
                class="form-control" 
                :placeholder="t('elements.placeholders.alk').value"
                step="0.1" 
                min="0" 
                max="20"
              >
            </div>
            <div class="form-group">
              <label for="alk_target">{{ t('elements.labels.targetAlk') }}</label>
              <input 
                type="number" 
                id="alk_target" 
                v-model="alkData.target" 
                class="form-control" 
                :placeholder="t('elements.placeholders.alk').value"
                step="0.1" 
                min="0" 
                max="20"
              >
            </div>
          </div>
          <div class="form-group">
            <label for="alk_unit">{{ t('elements.labels.unit') }}</label>
            <select 
              id="alk_unit" 
              v-model="alkData.unit" 
              class="select-control"
            >
              <option value="dKH">dKH</option>
              <option value="meq/L">meq/L</option>
            </select>
          </div>
        </div>
        
        <div class="form-section">
          <h4>{{ t('elements.sections.additiveSelection') }}</h4>
          <div class="form-group">
            <label for="alk_brand">{{ t('elements.labels.brand') }}</label>
            <select
              id="alk_brand"
              v-model="alkData.brand"
              class="select-control"
            >
              <option value="brs-2part">{{ t('elements.brands.alk.brs-2part') }}</option>
              <option value="red-sea-foundation-a">{{ t('elements.brands.alk.red-sea-foundation-a') }}</option>
              <option value="diy-nahco3">{{ t('elements.brands.alk.diy-nahco3') }}</option>
              <option value="pure-na2co3">{{ t('elements.brands.alk.pure-na2co3') }}</option>
              <option value="custom">{{ t('elements.brands.alk.custom') }}</option>
            </select>
          </div>
          
          <div v-if="alkData.brand === 'custom'" class="form-group">
            <label for="alk_custom_concentration">{{ t('elements.labels.customConcentration') }} (dKH/mL)</label>
            <input 
              type="number" 
              id="alk_custom_concentration" 
              v-model="alkData.custom_concentration" 
              class="form-control" 
              :placeholder="t('elements.placeholders.customConcentration').value"
              step="0.1" 
              min="0.1" 
              max="10"
            >
          </div>
        </div>
        
        <button 
          class="calculate-btn" 
          @click="calculateAlkDosage"
          :disabled="!canCalculateAlk"
        >
          {{ t('elements.buttons.calculateAlk') }}
        </button>
      </div>
      
      <!-- 钙计算 -->
      <div v-if="activeElement === 'ca'" class="calculator-form">
        <div class="form-section">
          <h4>{{ t('elements.sections.basicInfo') }}</h4>
          <div class="form-group">
            <label for="ca_volume">{{ t('elements.labels.volume') }}</label>
            <div class="input-with-action">
              <input
                type="number"
                id="ca_volume"
                v-model="caData.volume"
                class="form-control"
                :placeholder="t('elements.placeholders.volume').value"
                step="0.1"
                min="1"
              >
              <button
                v-if="storedWaterVolume"
                class="use-stored-btn"
                @click="useStoredVolume('ca')"
                type="button"
              >
                {{ t('elements.labels.useStoredVolume') }}
              </button>
            </div>
          </div>
        </div>
        
        <div class="form-section">
          <h4>{{ t('elements.sections.waterParams') }}</h4>
          <div class="form-row">
            <div class="form-group">
              <label for="ca_current">{{ t('elements.labels.currentCa') }}</label>
              <input
                type="number"
                id="ca_current"
                v-model="caData.current"
                class="form-control"
                :placeholder="t('elements.placeholders.ca').value"
                step="1"
                min="0"
                max="1000"
              >
            </div>
            <div class="form-group">
              <label for="ca_target">{{ t('elements.labels.targetCa') }}</label>
              <input
                type="number"
                id="ca_target"
                v-model="caData.target"
                class="form-control"
                :placeholder="t('elements.placeholders.ca').value"
                step="1"
                min="0"
                max="1000"
              >
            </div>
          </div>
        </div>
        
        <div class="form-section">
          <h4>{{ t('elements.sections.additiveSelection') }}</h4>
          <div class="form-group">
            <label for="ca_brand">{{ t('elements.labels.brand') }}</label>
            <select
              id="ca_brand"
              v-model="caData.brand"
              class="select-control"
            >
              <option value="brs-2part">{{ t('elements.brands.ca.brs-2part') }}</option>
              <option value="red-sea-foundation-b">{{ t('elements.brands.ca.red-sea-foundation-b') }}</option>
              <option value="diy-cacl2">{{ t('elements.brands.ca.diy-cacl2') }}</option>
              <option value="pure-cacl2">{{ t('elements.brands.ca.pure-cacl2') }}</option>
              <option value="custom">{{ t('elements.brands.ca.custom') }}</option>
            </select>
          </div>
          
          <div v-if="caData.brand === 'custom'" class="form-group">
            <label for="ca_custom_concentration">{{ t('elements.labels.customConcentration') }} (ppm/mL)</label>
            <input
              type="number"
              id="ca_custom_concentration"
              v-model="caData.custom_concentration"
              class="form-control"
              :placeholder="t('elements.placeholders.customConcentration').value"
              step="1"
              min="1"
              max="100"
            >
          </div>
        </div>
        
        <button
          class="calculate-btn"
          @click="calculateCaDosage"
          :disabled="!canCalculateCa"
        >
          {{ t('elements.buttons.calculateCa') }}
        </button>
      </div>
      
      <!-- 镁计算 -->
      <div v-if="activeElement === 'mg'" class="calculator-form">
        <div class="form-section">
          <h4>{{ t('elements.sections.basicInfo') }}</h4>
          <div class="form-group">
            <label for="mg_volume">{{ t('elements.labels.volume') }}</label>
            <div class="input-with-action">
              <input
                type="number"
                id="mg_volume"
                v-model="mgData.volume"
                class="form-control"
                :placeholder="t('elements.placeholders.volume').value"
                step="0.1"
                min="1"
              >
              <button
                v-if="storedWaterVolume"
                class="use-stored-btn"
                @click="useStoredVolume('mg')"
                type="button"
              >
                {{ t('elements.labels.useStoredVolume') }}
              </button>
            </div>
          </div>
        </div>
        
        <div class="form-section">
          <h4>{{ t('elements.sections.waterParams') }}</h4>
          <div class="form-row">
            <div class="form-group">
              <label for="mg_current">{{ t('elements.labels.currentMg') }}</label>
              <input
                type="number"
                id="mg_current"
                v-model="mgData.current"
                class="form-control"
                :placeholder="t('elements.placeholders.mg').value"
                step="1"
                min="0"
                max="2000"
              >
            </div>
            <div class="form-group">
              <label for="mg_target">{{ t('elements.labels.targetMg') }}</label>
              <input
                type="number"
                id="mg_target"
                v-model="mgData.target"
                class="form-control"
                :placeholder="t('elements.placeholders.mg').value"
                step="1"
                min="0"
                max="2000"
              >
            </div>
          </div>
        </div>
        
        <div class="form-section">
          <h4>{{ t('elements.sections.additiveSelection') }}</h4>
          <div class="form-group">
            <label for="mg_brand">{{ t('elements.labels.brand') }}</label>
            <select
              id="mg_brand"
              v-model="mgData.brand"
              class="select-control"
            >
              <option value="brs-2part">{{ t('elements.brands.mg.brs-2part') }}</option>
              <option value="diy-mgso4">{{ t('elements.brands.mg.diy-mgso4') }}</option>
              <option value="pure-mgso4">{{ t('elements.brands.mg.pure-mgso4') }}</option>
              <option value="custom">{{ t('elements.brands.mg.custom') }}</option>
            </select>
          </div>
          
          <div v-if="mgData.brand === 'custom'" class="form-group">
            <label for="mg_custom_concentration">{{ t('elements.labels.customConcentration') }} (ppm/mL)</label>
            <input
              type="number"
              id="mg_custom_concentration"
              v-model="mgData.custom_concentration"
              class="form-control"
              :placeholder="t('elements.placeholders.customConcentration').value"
              step="0.1"
              min="0.1"
              max="20"
            >
          </div>
        </div>
        
        <button
          class="calculate-btn"
          @click="calculateMgDosage"
          :disabled="!canCalculateMg"
        >
          {{ t('elements.buttons.calculateMg') }}
        </button>
      </div>
      
      <!-- 钾计算 -->
      <div v-if="activeElement === 'k'" class="calculator-form">
        <div class="form-section">
          <h4>{{ t('elements.sections.basicInfo') }}</h4>
          <div class="form-group">
            <label for="k_volume">{{ t('elements.labels.volume') }}</label>
            <div class="input-with-action">
              <input
                type="number"
                id="k_volume"
                v-model="kData.volume"
                class="form-control"
                :placeholder="t('elements.placeholders.volume').value"
                step="0.1"
                min="1"
              >
              <button
                v-if="storedWaterVolume"
                class="use-stored-btn"
                @click="useStoredVolume('k')"
                type="button"
              >
                {{ t('elements.labels.useStoredVolume') }}
              </button>
            </div>
          </div>
        </div>
        
        <div class="form-section">
          <h4>{{ t('elements.sections.waterParams') }}</h4>
          <div class="form-row">
            <div class="form-group">
              <label for="k_current">{{ t('elements.labels.currentK') }}</label>
              <input
                type="number"
                id="k_current"
                v-model="kData.current"
                class="form-control"
                :placeholder="t('elements.placeholders.k').value"
                step="1"
                min="0"
                max="500"
              >
            </div>
            <div class="form-group">
              <label for="k_target">{{ t('elements.labels.targetK') }}</label>
              <input
                type="number"
                id="k_target"
                v-model="kData.target"
                class="form-control"
                :placeholder="t('elements.placeholders.k').value"
                step="1"
                min="0"
                max="500"
              >
            </div>
          </div>
        </div>
        
        <div class="form-section">
          <h4>{{ t('elements.sections.additiveSelection') }}</h4>
          <div class="form-group">
            <label for="k_brand">{{ t('elements.labels.brand') }}</label>
            <select
              id="k_brand"
              v-model="kData.brand"
              class="select-control"
            >
              <option value="pure-kcl">{{ t('elements.brands.k.pure-kcl') }}</option>
              <option value="custom">{{ t('elements.brands.k.custom') }}</option>
            </select>
          </div>
          
          <div v-if="kData.brand === 'custom'" class="form-group">
            <label for="k_custom_concentration">{{ t('elements.labels.customConcentration') }} (ppm/mL)</label>
            <input
              type="number"
              id="k_custom_concentration"
              v-model="kData.custom_concentration"
              class="form-control"
              :placeholder="t('elements.placeholders.customConcentration').value"
              step="0.1"
              min="1"
              max="100"
            >
          </div>
        </div>
        
        <button
          class="calculate-btn"
          @click="calculateKDosage"
          :disabled="!canCalculateK"
        >
          {{ t('elements.buttons.calculateK') }}
        </button>
      </div>
      
      <!-- 计算结果 -->
      <div v-if="results.show" class="results-section">
        <div class="results-card" :class="{ 'warning': !results.isSafe }">
          <h4>{{ t('elements.results.fullTitle', { element: results.elementName }) }}</h4>
          
          <div class="result-grid">
            <div class="result-item">
              <span class="result-label">{{ t('elements.results.dosage') }}:</span>
              <span class="result-value">{{ results.dosageML }} mL</span>
            </div>
            <div class="result-item">
              <span class="result-label">{{ t('elements.results.concentrationChange') }}:</span>
              <span class="result-value">{{ results.dailyChange }} {{ results.unit }}</span>
            </div>
            <div class="result-item">
              <span class="result-label">{{ t('elements.results.concentrationUsed') }}:</span>
              <span class="result-value">{{ results.concentrationUsed }} {{ results.concentrationUnit }}</span>
            </div>
          </div>
          
          <div v-if="results.additionalInfo" class="additional-info">
            <p>{{ results.additionalInfo }}</p>
          </div>
          
          <div v-if="results.safetyNote" class="safety-note" :class="{ 'warning': !results.isSafe }">
            <h5>{{ t('elements.results.safetyNote') }}</h5>
            <p>{{ results.safetyNote }}</p>
          </div>
          
          <div class="calculation-note">
            <p><strong>{{ t('elements.results.calculationNote') }}:</strong></p>
            <ul>
              <li>{{ t('elements.results.note1') }}</li>
              <li>{{ t('elements.results.note2') }}</li>
              <li>{{ t('elements.results.note3') }}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { useCalculatorStore } from '../stores/calculator'
import { useI18n } from '../composables/useI18n'

const calculatorStore = useCalculatorStore()
const { t } = useI18n()

// 标签页状态
const activeElement = ref<'alk' | 'ca' | 'mg' | 'k'>('alk')

// 碱度数据
const alkData = reactive({
  volume: '',
  current: '',
  target: '',
  unit: 'dKH',
  brand: 'brs-2part',
  custom_concentration: '2.8'
})

// 钙数据
const caData = reactive({
  volume: '',
  current: '',
  target: '',
  brand: 'brs-2part',
  custom_concentration: '35'
})

// 镁数据
const mgData = reactive({
  volume: '',
  current: '',
  target: '',
  brand: 'brs-2part',
  custom_concentration: '9.8'
})

// 钾数据
const kData = reactive({
  volume: '',
  current: '',
  target: '',
  brand: 'pure-kcl',
  custom_concentration: '52.4'
})

// 计算结果
const results = reactive({
  show: false,
  elementName: '',
  dosageML: 0,
  dailyChange: 0,
  concentrationUsed: 0,
  concentrationUnit: '',
  additionalInfo: '',
  safetyNote: '',
  isSafe: true,
  unit: ''
})

// 计算属性
const storedWaterVolume = computed(() => {
  return calculatorStore.waterVolume
})

const canCalculateAlk = computed(() => {
  return alkData.volume && alkData.current && alkData.target
})

const canCalculateCa = computed(() => {
  return caData.volume && caData.current && caData.target
})

const canCalculateMg = computed(() => {
  return mgData.volume && mgData.current && mgData.target
})

const canCalculateK = computed(() => {
  return kData.volume && kData.current && kData.target
})

// 使用已保存的水体体积
const useStoredVolume = (element: string) => {
  if (storedWaterVolume.value) {
    const volume = storedWaterVolume.value.toString()
    if (element === 'alk') alkData.volume = volume
    else if (element === 'ca') caData.volume = volume
    else if (element === 'mg') mgData.volume = volume
    else if (element === 'k') kData.volume = volume
  }
}

// 计算碱度剂量
const calculateAlkDosage = () => {
  const volume = parseFloat(alkData.volume)
  const current = parseFloat(alkData.current)
  const target = parseFloat(alkData.target)
  const customConcentration = alkData.brand === 'custom' ? parseFloat(alkData.custom_concentration) : null

  const calculation = calculatorStore.calculateElementDosage(
    'alk',
    current,
    target,
    volume,
    alkData.brand,
    alkData.unit,
    customConcentration
  )

  updateResults('碱度', calculation, 'dKH', 'dKH/mL')
}

// 计算钙剂量
const calculateCaDosage = () => {
  const volume = parseFloat(caData.volume)
  const current = parseFloat(caData.current)
  const target = parseFloat(caData.target)
  const customConcentration = caData.brand === 'custom' ? parseFloat(caData.custom_concentration) : null

  const calculation = calculatorStore.calculateElementDosage(
    'ca',
    current,
    target,
    volume,
    caData.brand,
    'ppm',
    customConcentration
  )

  updateResults('钙', calculation, 'ppm', 'ppm/mL')
}

// 计算镁剂量
const calculateMgDosage = () => {
  const volume = parseFloat(mgData.volume)
  const current = parseFloat(mgData.current)
  const target = parseFloat(mgData.target)
  const customConcentration = mgData.brand === 'custom' ? parseFloat(mgData.custom_concentration) : null

  const calculation = calculatorStore.calculateElementDosage(
    'mg',
    current,
    target,
    volume,
    mgData.brand,
    'ppm',
    customConcentration
  )

  updateResults('镁', calculation, 'ppm', 'ppm/mL')
}

// 计算钾剂量
const calculateKDosage = () => {
  const volume = parseFloat(kData.volume)
  const current = parseFloat(kData.current)
  const target = parseFloat(kData.target)
  const customConcentration = kData.brand === 'custom' ? parseFloat(kData.custom_concentration) : null

  const calculation = calculatorStore.calculateElementDosage(
    'k',
    current,
    target,
    volume,
    kData.brand,
    'ppm',
    customConcentration
  )

  updateResults('钾', calculation, 'ppm', 'ppm/mL')
}

// 更新计算结果
const updateResults = (
  elementName: string,
  calculation: any,
  unit: string,
  concentrationUnit: string
) => {
  results.elementName = elementName
  results.dosageML = calculation.dosageML
  results.dailyChange = calculation.dailyChange
  results.concentrationUsed = calculation.concentrationUsed
  results.concentrationUnit = concentrationUnit
  results.additionalInfo = calculation.additionalInfo
  results.safetyNote = calculation.safetyNote
  results.isSafe = calculation.isSafe
  results.unit = unit
  results.show = true
}

// 组件挂载
onMounted(() => {
  calculatorStore.getWaterVolume()
})
</script>

<style scoped>
@media (max-width: 768px) {
  .calculator-tabs{
    margin: 0px 0 15px 0 !important;
  }
}
</style>